// Mobile Inbox: search, the bucket picker (Primary / Other / Later / Cleared, as a sheet), a filter
// menu, and one row per task or conversation grouped by day. Tapping a row opens it and marks it read.
import '@/styles/inbox.css';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AtSign, ChevronDown, Hash, ListFilter, MessageCircle, Search, X } from '@/components/ui/icons';
import { useDb, useMe } from '@/lib/store';
import { useOpenTask, useWorkspaceId } from '@/lib/nav';
import { markNotifications } from '@/lib/actions';
import { format, isToday, isYesterday } from 'date-fns';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { INBOX_FILTERS, INBOX_TABS, belongsToWorkspace, bucketize, channelTitle, groupNotifications, inTab, matchesFilter, type InboxGroup } from '@/pages/inbox/model';
import { useInboxPrefs } from '@/pages/inbox/prefs';
import { TaskStatusIcon } from '@/pages/inbox/InboxRow';
import { describeNotification, Snippet } from '@/pages/inbox/describe';
import { TopHeader } from './MobileHome';
import { openSheet } from './sheets';

/** 5:11 PM today, then Yesterday, a weekday this week, else Nov 3. */
function shortWhen(ts: number) {
  if (isToday(ts)) return format(ts, 'h:mm a');
  if (isYesterday(ts)) return 'Yesterday';
  return Date.now() - ts < 6 * 86_400_000 ? format(ts, 'EEE') : format(ts, 'MMM d');
}

const TALK = new Set(['mention', 'comment', 'reply', 'assigned_comment']);

function Row({ group }: { group: InboxGroup }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const db = useDb((d) => d);
  const navigate = useNavigate();
  const openTask = useOpenTask();
  const n = group.latest;
  const task = group.taskId ? db.tasks[group.taskId] : undefined;
  const channel = group.channelId ? db.channels[group.channelId] : undefined;
  const title = task ? task.name : group.channelId ? channelTitle(db, group.channelId, me) : 'Notification';
  const actor = n.actorId === me ? 'You' : (db.users[n.actorId]?.name.split(' ')[0] ?? 'Someone');
  const snippet = typeof n.data?.snippet === 'string' ? n.data.snippet : null;
  const open = () => {
    const unread = group.items.filter((x) => !x.readAt).map((x) => x.id);
    if (unread.length) markNotifications(unread, { readAt: Date.now() });
    if (task) openTask(task.id);
    else if (group.channelId) navigate(`/${ws}/chat/${group.channelId}${typeof n.data?.messageId === 'string' ? `?m=${n.data.messageId}` : ''}`);
  };
  return (
    <button type="button" className={clsx('m-ib-row', { unread: group.unread })} onClick={open}>
      <span className="m-ib-icon">{task ? <TaskStatusIcon db={db} task={task} size={20} /> : channel?.kind === 'dm' ? <MessageCircle size={19} /> : <Hash size={19} />}</span>
      <span className="m-ib-text">
        <span className="m-ib-title truncate">{title}</span>
        <span className="m-ib-desc">
          <span className="truncate">
            {snippet && TALK.has(n.kind) ? (
              <>
                {actor}: <Snippet text={snippet} db={db} me={me} />
              </>
            ) : (
              <>
                {actor} {describeNotification(db, n, me)}
              </>
            )}
          </span>
          {n.kind === 'mention' && <AtSign size={15} className="m-ib-at" />}
          <span className="m-ib-time">{shortWhen(n.createdAt)}</span>
        </span>
      </span>
    </button>
  );
}

export function MobileInbox() {
  const ws = useWorkspaceId();
  const me = useMe();
  const db = useDb((d) => d);
  const [prefs, setPrefs] = useInboxPrefs();
  const { tab, filter, unreadOnly } = prefs;
  const [q, setQ] = useState('');

  const mine = useMemo(() => Object.values(db.notifications).filter((n) => n.userId === me && belongsToWorkspace(db, n, ws)), [db, me, ws]);
  const unread = mine.filter((n) => !n.readAt && inTab(n, tab)).length;
  const otherUnread = mine.some((n) => !n.readAt && n.bucket === 'other' && inTab(n, 'other'));
  const text = q.trim().toLowerCase();
  const visible = useMemo(
    () =>
      mine.filter((n) => {
        if (!inTab(n, tab) || !matchesFilter(n, filter) || (unreadOnly && n.readAt)) return false;
        if (!text) return true;
        const task = n.taskId ? db.tasks[n.taskId] : undefined;
        const name = task?.name ?? (typeof n.data?.channelId === 'string' ? channelTitle(db, n.data.channelId, me) : '');
        return name.toLowerCase().includes(text) || String(n.data?.snippet ?? '').toLowerCase().includes(text);
      }),
    [mine, tab, filter, unreadOnly, text, db, me],
  );
  const buckets = useMemo(() => bucketize(groupNotifications(visible, prefs.groupByTask)), [visible, prefs.groupByTask]);
  const tabMeta = INBOX_TABS.find((t) => t.key === tab)!;
  const filtered = filter !== 'all' || unreadOnly;

  return (
    <div className="m-page">
      <TopHeader title="Inbox" />
      <div className="m-pad">
        <label className="m-search">
          <Search size={18} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" />
          {q && (
            <button type="button" className="m-icon-btn sm" onClick={() => setQ('')} aria-label="Clear search">
              <X size={16} />
            </button>
          )}
        </label>
      </div>
      <div className="m-sheet-card">
        <div className="m-ib-bar">
          <button type="button" className="m-ib-tab" onClick={() => openSheet('inbox-tabs')}>
            {tabMeta.label}
            {unread > 0 && tab !== 'cleared' && <span className="m-ib-count">{unread > 99 ? '99+' : unread}</span>}
            <ChevronDown size={16} />
            {tab !== 'other' && otherUnread && <span className="m-dot" aria-label="Unread in Other" />}
          </button>
          <span className="spacer" />
          <Popover
            placement="bottom-end"
            content={(close) => (
              <Menu style={{ width: 230 }}>
                <MenuLabel>Show</MenuLabel>
                {INBOX_FILTERS.map((f) => (
                  <MenuItem key={f.key} label={f.label} checked={filter === f.key} onClick={() => (setPrefs({ filter: f.key }), close())} />
                ))}
                <MenuSeparator />
                <MenuItem label="Unread only" checked={unreadOnly} onClick={() => setPrefs({ unreadOnly: !unreadOnly })} />
                <MenuItem label="Group by task" checked={prefs.groupByTask} onClick={() => setPrefs({ groupByTask: !prefs.groupByTask })} />
                <MenuSeparator />
                <MenuItem
                  label="Mark all as read"
                  onClick={() => {
                    const ids = visible.filter((n) => !n.readAt).map((n) => n.id);
                    if (ids.length) markNotifications(ids, { readAt: Date.now() });
                    close();
                  }}
                />
              </Menu>
            )}
          >
            <button type="button" className={clsx('m-icon-btn', { on: filtered })} aria-label="Filter">
              <ListFilter size={20} />
            </button>
          </Popover>
        </div>
        <div className="m-scroll">
          {buckets.map((b) => (
            <section key={b.key}>
              <div className="m-ib-day">{b.label}</div>
              {b.rows.map((g) => (
                <Row key={g.key} group={g} />
              ))}
            </section>
          ))}
          {buckets.length === 0 && (
            <div className="m-empty">
              <strong>{text ? 'No matches' : tabMeta.empty}</strong>
              <span>{text ? 'Try another search.' : tabMeta.emptyHint}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
